import Dexie, { type Table } from 'dexie'

/**
 * The browser-side store (SPEC section 2): a draft queue for records created with
 * no signal, and a snapshot of the catalogue and clients so search works in the
 * orchard. The service worker caches API reads for pages you've visited; this
 * covers the two things it can't — a global search over everything, and writes
 * that must survive until the connection comes back.
 *
 * Deliberately NOT a sync engine (SPEC section 2). New records only, no edits —
 * so nothing here can ever conflict with another device.
 */

export interface OutboxItem {
  id?: number
  // What to create, and where to POST it.
  kind: 'client' | 'note' | 'quote'
  path: string
  body: Record<string, unknown>
  // Idempotency key — the server upserts by this, so a double replay is a no-op
  // (blueprint §4.9: one id, one offer, one number).
  clientRequestId: string
  // A local uuid for an entity created offline, so a note/quote queued for it can
  // reference it before the server has assigned a real id.
  localEntityId?: string
  // The real id the server assigned this entity. Recorded when it syncs, so a
  // dependent record replayed in a LATER run can still resolve the reference
  // (the mapping outlives the run that created it).
  serverId?: string
  status: 'pending' | 'done'
  error?: string
  createdAt: number
}

export interface CachedClient {
  id: string
  name: string
  city: string | null
  nip: string | null
  phone: string | null
  searchable: string
}

export interface CachedMachine {
  id: string
  model: string
  catalogCode: string
  manufacturerName: string
  category: string
  searchable: string
}

class OfflineDb extends Dexie {
  outbox!: Table<OutboxItem, number>
  clients!: Table<CachedClient, string>
  machines!: Table<CachedMachine, string>

  constructor() {
    super('crm-warka')
    this.version(1).stores({
      // ++id = autoincrement, giving a stable replay order (creation order).
      outbox: '++id, status, clientRequestId, localEntityId',
      clients: 'id, searchable',
      machines: 'id, searchable',
    })
  }
}

export const db = new OfflineDb()

/** Wipes everything — called on logout so the next user on a shared laptop starts clean. */
export async function clearOfflineData(): Promise<void> {
  await Promise.all([db.outbox.clear(), db.clients.clear(), db.machines.clear()])
}
